import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import range from "lodash/range";
import pluralize from "pluralize";
import {useEffect} from "react";
import {FiChevronLeft, FiChevronRight} from "react-icons/fi";
import {HiEllipsisHorizontal} from "react-icons/hi2";

import Button from "@/components/button/Button.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {Select} from "@/components/select/Select.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {useUiSettings} from "@/lib/hooks/useUiSettings.ts";

import styles from "./Paginator.module.scss";

export interface LimitAndOffset {
  limit: number;
  offset: number;
}

interface PaginatorProps extends LimitAndOffset {
  className?: string;
  total: number;
  options?: SelectOption<number>[];
  hideOptions?: boolean;
  hideIcons?: boolean;
  maxVisiblePages?: number;
  updatePagination: (laf: LimitAndOffset) => void;
  data?: unknown[]; // Used to check if current page is empty and reset offset
  resultsRef?: React.RefObject<HTMLDivElement>;
}

export const Paginator = ({
  className,
  offset,
  limit,
  total,
  updatePagination,
  options,
  hideOptions,
  maxVisiblePages = 3,
  data,
  resultsRef,
}: PaginatorProps) => {
  const {updateUiSettings} = useUiSettings();
  const pagesAvailable = Math.ceil(total / limit);
  const activePage = Math.floor(offset / limit) + 1;

  if (!options) {
    options = [
      {value: 5, label: "5 Per Page"},
      {value: 10, label: "10 Per Page"},
      {value: 25, label: "25 Per Page"},
      {value: 50, label: "50 Per Page"},
      {value: 100, label: "100 Per Page"},
      {value: 250, label: "250 Per Page"},
    ];
  }
  const defaultOption = options.find(({value}) => value === limit) || options[0];

  const savePageSize = (newSize: number) => {
    updateUiSettings({table_page_size: newSize});
  };

  const scrollToResultsIfNeeded = () => {
    if (!resultsRef) {
      return;
    }

    const element = resultsRef.current;
    if (element) {
      const rect = element.getBoundingClientRect();
      const headerHeight = (document.querySelector("#upHeaderNav") as HTMLElement)?.offsetHeight || 80;
      window.scrollTo({top: window.scrollY + rect.top - headerHeight - 20, behavior: "smooth"});
    }
  };

  useEffect(() => {
    if (data && isEmpty(data) && total > 0 && offset > 0) {
      updatePagination({offset: 0, limit});
    }
  }, [data, limit, offset, total, updatePagination]);

  const generatePageNumbers = (): (number | string)[] => {
    if (maxVisiblePages < 7) {
      const visiblePages = Math.min(maxVisiblePages, pagesAvailable);
      const startPage = Math.min(
        pagesAvailable - visiblePages,
        Math.max(0, activePage - Math.ceil(maxVisiblePages / 2))
      );
      const endPage = Math.min(pagesAvailable, startPage + visiblePages);
      return range(startPage + 1, endPage + 1);
    }

    if (pagesAvailable <= maxVisiblePages) {
      return range(1, pagesAvailable + 1);
    }

    if (activePage <= maxVisiblePages - 3) {
      return [...range(1, maxVisiblePages - 1), "ellipsis-end", pagesAvailable];
    }

    if (activePage >= pagesAvailable - (maxVisiblePages - 4)) {
      return [1, "ellipsis-start", ...range(pagesAvailable - (maxVisiblePages - 3), pagesAvailable + 1)];
    }

    const startPage = activePage - Math.floor((maxVisiblePages - 5) / 2);
    const endPage = activePage + Math.ceil((maxVisiblePages - 5) / 2);

    return [1, "ellipsis-start", ...range(startPage, endPage + 1), "ellipsis-end", pagesAvailable];
  };

  const pageNumbers = generatePageNumbers();

  return (
    <div
      className={classNames(styles.paginationContainer, className ?? "mb-4", {"justify-content-between": !hideOptions})}
    >
      {!hideOptions && (
        <div className={styles.paginationOptions}>
          <Select
            menuPortalTarget={
              (document.querySelector(`div.outlet-container > div.content-container`) as HTMLElement) ?? document.body
            }
            menuPosition={"fixed"}
            className={styles.paginationOptionsSelect}
            defaultValue={defaultOption}
            onChange={(v) => {
              if (!v) {
                return;
              }
              savePageSize(v.value);
              updatePagination({offset: 0, limit: v.value});
            }}
            options={options}
          />
          {total > 0 ? (
            <div className={"pulseTextBodySmall"}>
              {total} Total {pluralize("Entry", total)}
            </div>
          ) : (
            <div className={"pulseTextBodySmall"}>0 Total Entries</div>
          )}
        </div>
      )}

      {pagesAvailable > 1 && (
        <div>
          <nav aria-label="Page number">
            <ul className="d-flex mb-0 ps-0">
              <IconButton
                icon={FiChevronLeft}
                variant={"paginator"}
                as={"li"}
                className={classNames({disabled: activePage <= 1})}
                onClick={() => {
                  updatePagination({offset: Math.max(offset - limit, 0), limit: limit});
                  scrollToResultsIfNeeded();
                }}
              />
              {pageNumbers.map((page) => {
                if (typeof page === "string") {
                  return (
                    <IconButton
                      icon={HiEllipsisHorizontal}
                      className={"disabled"}
                      typography={"normal"}
                      key={`page-${page}`}
                      variant={"paginator"}
                      as={"li"}
                    />
                  );
                }
                return (
                  <Button
                    typography={"normal"}
                    key={`page-${page}`}
                    variant={"paginator"}
                    className={classNames({active: page === activePage})}
                    as={"li"}
                    onClick={() => {
                      updatePagination({offset: Math.max(limit * (page - 1), 0), limit});
                      scrollToResultsIfNeeded();
                    }}
                  >
                    {page}
                  </Button>
                );
              })}

              <IconButton
                variant={"paginator"}
                icon={FiChevronRight}
                as={"li"}
                className={classNames({disabled: activePage + 1 > pagesAvailable})}
                onClick={() => {
                  updatePagination({offset: offset + limit, limit});
                  scrollToResultsIfNeeded();
                }}
              />
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};
